import { Button, Loader, Menu, Stack, Text, ThemeIcon } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
    Icon,
    iconArrowUpRight,
    iconChatGPT,
    iconCheck,
    iconChevronDown,
    iconClaude,
    iconCopy,
    iconText,
    useStable,
} from "@surrealdb/ui";
import { useMemo, useState } from "react";
import classes from "./style.module.scss";

const LLM_PREFIXES = {
    chatGpt: "https://chatgpt.com/?hints=search&q=",
    claude: "https://claude.ai/new?q=",
};

type LLM = keyof typeof LLM_PREFIXES;

/** URL that serves the current page as raw markdown. */
function markdownUrl(): string {
    return `${location.pathname}.md`;
}

export function CopyPageMenu() {
    const { copy, copied } = useClipboard();
    const [isFetching, setIsFetching] = useState(false);

    const handleCopyMarkdown = useStable(async () => {
        setIsFetching(true);

        try {
            const response = await fetch(markdownUrl());
            const body = await response.text();

            copy(body);
            window.dataLayer?.push({
                event: "copy_page_markdown",
                page_path: window.location.pathname,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setIsFetching(false);
        }
    });

    const handleViewMarkdown = useStable(() => {
        window.open(markdownUrl(), "_blank", "noopener,noreferrer");
    });

    const handleOpenLLM = useStable((llm: LLM) => {
        const prompt = encodeURIComponent(
            `Read from ${location.href} so I can ask questions about it.`,
        );

        window.open(`${LLM_PREFIXES[llm]}${prompt}`, "_blank", "noopener,noreferrer");
    });

    const leftSection = useMemo(() => {
        if (isFetching) {
            return <Loader size={12} />;
        }

        if (copied) {
            return (
                <Icon
                    path={iconCheck}
                    size="sm"
                />
            );
        }

        return (
            <Icon
                path={iconCopy}
                size="sm"
            />
        );
    }, [isFetching, copied]);

    return (
        <Menu
            shadow="md"
            width={280}
            withinPortal
            trigger="click-hover"
            transitionProps={{
                transition: "scale-y",
            }}
        >
            <Menu.Target>
                {/* The chevron sits in its own compartment behind a rule, as on
                    the reference docs, rather than floating inside the label. */}
                <Button
                    size="xs"
                    className={classes.copyButton}
                    leftSection={leftSection}
                    rightSection={
                        <Icon
                            path={iconChevronDown}
                            size="xs"
                        />
                    }
                >
                    Copy page
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item
                    className={classes.menuItem}
                    leftSection={
                        <ThemeIcon>
                            <Icon
                                path={iconCopy}
                                opacity={1}
                                size="lg"
                            />
                        </ThemeIcon>
                    }
                    onClick={handleCopyMarkdown}
                >
                    <Stack gap={0}>
                        <Text
                            fz="sm"
                            fw={500}
                        >
                            Copy as Markdown
                        </Text>
                        <Text
                            fz="xs"
                            opacity={0.6}
                        >
                            Copy page content for LLMs
                        </Text>
                    </Stack>
                </Menu.Item>
                <Menu.Item
                    className={classes.menuItem}
                    leftSection={
                        <ThemeIcon>
                            <Icon
                                path={iconText}
                                opacity={1}
                                size="lg"
                            />
                        </ThemeIcon>
                    }
                    rightSection={
                        <Icon
                            path={iconArrowUpRight}
                            size="sm"
                        />
                    }
                    onClick={handleViewMarkdown}
                >
                    <Stack gap={0}>
                        <Text
                            fz="sm"
                            fw={500}
                        >
                            View as Markdown
                        </Text>
                        <Text
                            fz="xs"
                            opacity={0.6}
                        >
                            Open the raw markdown page
                        </Text>
                    </Stack>
                </Menu.Item>
                <Menu.Item
                    className={classes.menuItem}
                    leftSection={
                        <ThemeIcon>
                            <Icon
                                path={iconChatGPT}
                                opacity={1}
                                size="lg"
                            />
                        </ThemeIcon>
                    }
                    rightSection={
                        <Icon
                            path={iconArrowUpRight}
                            size="sm"
                        />
                    }
                    onClick={() => handleOpenLLM("chatGpt")}
                >
                    <Stack gap={0}>
                        <Text
                            fz="sm"
                            fw={500}
                        >
                            Open in ChatGPT
                        </Text>
                        <Text
                            fz="xs"
                            opacity={0.6}
                        >
                            Ask questions about this page
                        </Text>
                    </Stack>
                </Menu.Item>
                <Menu.Item
                    className={classes.menuItem}
                    leftSection={
                        <ThemeIcon>
                            <Icon
                                path={iconClaude}
                                opacity={1}
                                size="lg"
                            />
                        </ThemeIcon>
                    }
                    rightSection={
                        <Icon
                            path={iconArrowUpRight}
                            size="sm"
                        />
                    }
                    onClick={() => handleOpenLLM("claude")}
                >
                    <Stack gap={0}>
                        <Text
                            fz="sm"
                            fw={500}
                        >
                            Open in Claude
                        </Text>
                        <Text
                            fz="xs"
                            opacity={0.6}
                        >
                            Ask questions about this page
                        </Text>
                    </Stack>
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
}
